import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { controlToastState, openModal, TabContainer } from "risa-oasis-ui_v2";
import { commentRpaModalSave } from "../../../../api/postCall/commentRpaModalSave";
import CommentRpaNewModal from "../../../../components/modals/commentRPAnewModal/commentRpaNewModal";
import HappyFlowModal from "../../../../components/modals/happyFlowModal/happyFlowModal";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { useInitialDataOfSingleMedicalOrder } from "../../../../context/initialDataOfSingleMedicalOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { AllCommentsProps } from "../../../../data-model/busniessOfficeNote";
import { CoverageDetailsData } from "../../../../data-model/coverage-details";
import { BusniessOfficeReportType } from "../../../../enums/busniessOfficeReportType";
import { CoverageStatus } from "../../../../enums/coverageStatus";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";
import { ModalId } from "../../../../enums/modalId";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { useLogJCodeStatusEvents } from "../../../../hooks/logJCodeStatusEvents";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { InfoIcon } from "../../../../svg/info-icon";
import WarningIcon from "../../../../svg/warningIcon";
import { getItemFromLocalStorage } from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { createBusniessOfficePostNotes } from "../../../singleOrderLayout/utils/createBusniessOfficeNoteData";
import { getAllJCodes } from "../../../singleOrderLayout/utils/existingDocsToLs";
import CoverageAmounts from "../coverageAmounts/coverageAmounts";
import InactiveInsurance from "../inactiveInsurance/inactiveInsurance";
import PrimaryInfo from "../primaryInfo/primaryInfo";
import SharedAmountCard from "../sharedAmountCard/sharedAmountCard";

interface CoverageProps {
  coverageData: CoverageDetailsData[];
  currentTabIndex?: (index: number) => void;
}

export const Coverage = (props: CoverageProps) => {
  const [tabsArray, setTabsArray] = useState<
    { id: string; label: string; tabContent: () => React.JSX.Element }[]
  >([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [
    writeBackNotesForInactiveInsurance,
    setWriteBackNotesForInactiveInsurance,
  ] = useState<AllCommentsProps>({
    comments: [],
    commentDate: "",
    type: BusniessOfficeReportType.FINAL,
  });
  const [queryTypeForInactiveInsurance, setQueryTypeForInactiveInsurance] =
    useState<string>("Authmate Case \n\nInsurance is inactive, please check");

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const org_id = getOrgIdForFetchExternalWorklist();

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const dispatch = useDispatch<AppDispatch>();

  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { manualAuthStatus } = useBusniessOfficeNotesContext();

  const logJCodeStatusEvents = useLogJCodeStatusEvents();
  const { oldAuthStatus: oldAuthStatusFromContext } =
    useInitialDataOfSingleMedicalOrder();

  const isEventTriggerRef = useRef(false);

  const getBoStatus = useMemo(() => {
    let boStatus = "";
    const orgId = getOrgIdForFetchExternalWorklist();
    if (orgId === OrganizationType.CHC) {
      boStatus = "Authmate Issue";
    } else {
      boStatus = "Insurance Issue";
    }
    return boStatus;
  }, [org_id]);

  const navigate = useNavigate();

  const handleHappyFlowModalSave = async () => {
    try {
      const planName = singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "";
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: org_id ?? "",
        event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          modal_name: ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
          master_auth_status_old:
            singleMedicalPaOrder?.status?.master_auth_status ?? "",
          master_auth_status_new:
            singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
          payer_name: payerName,
          plan_name: planName,
          j_code: getItemFromLocalStorage(
            `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
          ).join(","),
        },
      });

      const allJCodes = getAllJCodes(singleMedicalPaOrder ?? {});
      if (allJCodes) {
        logJCodeStatusEvents(allJCodes, 1000);
      }

      setIsLoading(true);
      const payload = {
        identifier: singleMedicalPaOrder?.id ?? "",
        org_id: org_id ?? "",
        bo_status: getBoStatus,
        overwrite_rpa_errors: false,
        write_back_reports: null,
        auth_status: manualAuthStatus ?? undefined,
      };
      await commentRpaModalSave(payload);
      setIsLoading(false);
      dispatch(closeModal());
      controlToastState("mark-as-completed-success");
      navigate(
        `/worklists/${getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE)}`,
      );
    } catch (error) {
      console.error("error", error);
      controlToastState("mark-as-completed-error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    props?.currentTabIndex?.(activeTabIndex);
  }, [activeTabIndex]);

  useEffect(() => {
    if (openedModalId === ModalId.COMMENT_RPA_MODAL_NEW) {
      openModal(ModalId.COMMENT_RPA_MODAL_NEW);
    }
    if (openedModalId === ModalId.HAPPY_FLOW_MODAL) {
      openModal(ModalId.HAPPY_FLOW_MODAL);
    }
  }, [openedModalId]);

  const handleCommentsChange = (comment: string) => {
    setQueryTypeForInactiveInsurance(comment);
    setWriteBackNotesForInactiveInsurance(
      createBusniessOfficePostNotes(
        singleMedicalPaOrder?.auth_on_file?.reports,
        comment,
        getBoStatus,
      ),
    );
  };

  useEffect(() => {
    if (singleMedicalPaOrder?.auth_on_file?.reports) {
      setWriteBackNotesForInactiveInsurance(
        createBusniessOfficePostNotes(
          singleMedicalPaOrder?.auth_on_file?.reports,
          "Authmate Case \n\nInsurance is inactive, please check",
          getBoStatus,
        ),
      );
    }
  }, [singleMedicalPaOrder?.auth_on_file?.reports]);

  useEffect(() => {
    if (tabsArray && tabsArray.length > 0 && !isEventTriggerRef.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id,
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: org_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          EventsName.INSURANCE_TAB_SELECTED +
          " (" +
          tabsArray[activeTabIndex]?.label +
          ")",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
          coverage_type: tabsArray[activeTabIndex]?.label ?? "",
          tab_index: activeTabIndex,
          coverage_status: singleMedicalPaOrder?.status?.ev_bv?.primary ?? "",
          primary_insurance_status:
            singleMedicalPaOrder?.coverage?.primary?.active ?? "",
          payer_name: payerName,
        },
      });
      isEventTriggerRef.current = true;
    }
  }, [activeTabIndex, tabsArray]);

  const createTabContent = (
    data: CoverageDetailsData,
  ): (() => React.JSX.Element) => {
    return () => (
      <div className="created-coverage-tab pt-3">
        <div className="created-coverage-left flex h-full flex-[30%] flex-col gap-4 overflow-auto border-r-2 border-dashed border-primaryGray-15 pr-5">
          {data?.primaryData && (
            <>
              {data.primaryData.map((primaryData, index) => (
                <PrimaryInfo
                  data={primaryData}
                  key={`${primaryData.header}-${index}`}
                />
              ))}
            </>
          )}
        </div>

        <div className="created-coverage-right h-full flex-[70%] overflow-auto pl-5">
          {data?.coverageStatus === CoverageStatus.ERROR && (
            <>
              <div className="coverage-fetching-error flex flex-col items-center gap-4 rounded-lg bg-tertiaryRed-11 p-4">
                <div className="error-icon">
                  <WarningIcon />
                </div>
                <div className="error-message text-small font-medium leading-6 text-tertiaryRed-1">
                  Human Intervention is required. Please visit the portal for
                  further details.
                </div>
              </div>
            </>
          )}

          {data?.coverageStatus === CoverageStatus.IN_PROGRESS && (
            <div className="coverage-fetching-error flex flex-col items-center gap-4 rounded-lg bg-tertiaryBlue-12 p-4">
              <div className="error-icon">
                <InfoIcon height="32" width="32" stroke="#007AFF" />
              </div>
              <div className="error-message text-small font-medium leading-6 text-tertiaryBlue-4">
                Coverage Information is being fetched. Please come back later.
              </div>
            </div>
          )}

          {data?.coverageStatus === CoverageStatus.EV_TYPE_NOT_SUPPORTED && (
            <div className="error-container flex h-full w-full flex-col items-center justify-center gap-2">
              <div className="error-icon">
                <InfoIcon height={"48"} width={"48"} stroke={"#0056D6"} />
              </div>
              <div className="error-message text-center text-h11 font-semiBold text-primaryGray-1">
                This plan type does not support coverage verification.
              </div>
            </div>
          )}

          {data?.isActive === "inactive" &&
            data?.coverageStatus !== CoverageStatus.ERROR && (
              <InactiveInsurance
                onCommentsChange={(comment) => handleCommentsChange(comment)}
                queryType={queryTypeForInactiveInsurance}
                status={
                  singleMedicalPaOrder?.status?.ev_write_back_status ?? ""
                }
                id={singleMedicalPaOrder?.id ?? ""}
                reports={singleMedicalPaOrder?.auth_on_file?.reports ?? []}
              />
            )}

          {data?.isActive === "active" &&
            data?.coverageStatus !== CoverageStatus.ERROR &&
            data?.coverageStatus !== CoverageStatus.IN_PROGRESS &&
            data?.coverageStatus !== CoverageStatus.EV_TYPE_NOT_SUPPORTED && (
              <>
                {data?.coverageAmountRelatedData?.map(
                  (amountData, amountIndex) => {
                    return (
                      <div className="coverage-amounts-data" key={amountIndex}>
                        {amountData.deductible && (
                          <CoverageAmounts
                            coverageAmountsData={amountData.deductible}
                            coverageType="Deductibles"
                          />
                        )}
                        {amountData.outOfPocket && (
                          <CoverageAmounts
                            coverageAmountsData={amountData.outOfPocket}
                            coverageType="Out of Pocket"
                            levelType={amountData.benefitLevel || ""}
                          />
                        )}
                      </div>
                    );
                  },
                )}
                {data?.sharedAmountData &&
                  data?.sharedAmountData?.length > 0 && (
                    <div className="shared-amounts--holder mt-3">
                      <SharedAmountCard
                        sharedAmountData={data?.sharedAmountData}
                        categoryName={data?.costSharingName}
                      />
                    </div>
                  )}
              </>
            )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const allTabsArray: Array<{
      id: string;
      label: string;
      tabContent: () => React.JSX.Element;
    }> = [];

    props.coverageData.slice(0, 10).forEach((coverage, index) => {
      let labelName = "";
      switch (index) {
        case 0:
          labelName = "Primary";
          break;
        case 1:
          labelName = "Secondary";
          break;
        case 2:
          labelName = "Tertiary";
          break;
        default:
          labelName = `Coverage ${index + 1}`;
      }

      allTabsArray.push({
        id: labelName.toLowerCase().replace(" ", "-"),
        label: `${labelName} : ${coverage?.payerName ?? "Unknown"}`,
        tabContent: createTabContent(coverage),
      });
    });

    setTabsArray(allTabsArray);
  }, [props.coverageData]);

  return (
    <>
      {tabsArray && tabsArray.length > 0 && (
        <TabContainer
          tabsDetails={tabsArray}
          tabContainerId={`coverages-tab`}
          currentActiveTabIndex={(index) => {
            setActiveTabIndex(index);
            apiWorkerManager.enqueueAndProcess({
              patient_id: singleMedicalPaOrder?.patient_id,
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: org_id ?? "",
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              event_name:
                EventsName.INSURANCE_TAB_SELECTED +
                " (" +
                tabsArray[index]?.label +
                ")",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                coverage_type: tabsArray[index]?.label ?? "",
                tab_index: index,
                coverage_status:
                  singleMedicalPaOrder?.status?.ev_bv?.primary ?? "",
                primary_insurance_status:
                  singleMedicalPaOrder?.status?.ev_bv?.primary ?? "",
                payer_name: payerName,
              },
            });
          }}
        />
      )}
      {openedModalId === ModalId.COMMENT_RPA_MODAL_NEW && (
        <CommentRpaNewModal
          onClose={() => dispatch(closeModal())}
          onSave={() => {
            apiWorkerManager.enqueueAndProcess({
              patient_id: singleMedicalPaOrder?.patient_id ?? "",
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: org_id ?? "",
              event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                master_auth_status_old: oldAuthStatusFromContext,
                master_auth_status_new:
                  singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
                payer_name: payerName,
                plan_name:
                  singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "",
                j_code: getItemFromLocalStorage(
                  `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
                ).join(","),
              },
            });
            const allJCodes = getAllJCodes(singleMedicalPaOrder ?? {});
            if (allJCodes) {
              logJCodeStatusEvents(allJCodes, 1000);
            }
            dispatch(closeModal());
            controlToastState("mark-as-completed-success");
            navigate(
              `/worklists/${getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE)}`,
            );
          }}
          wrtiteBackStatus={
            singleMedicalPaOrder?.status?.ev_write_back_status ?? ""
          }
          docRpaStatus={""}
          id={singleMedicalPaOrder?.id ?? ""}
          orderId={singleMedicalPaOrder?.id ?? ""}
          writeBackReports={[writeBackNotesForInactiveInsurance]}
          auth_status={manualAuthStatus || undefined}
        />
      )}
      {openedModalId === ModalId.HAPPY_FLOW_MODAL && (
        <HappyFlowModal
          onClose={() => dispatch(closeModal())}
          onSave={handleHappyFlowModalSave}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
